import { useState } from 'react';
import { toast } from 'sonner';
import type { ClientDeletionStep } from '@hangar/shared';
import { useClientMutations } from '@/api/hooks';
import type { ClientOrg, ClientOrgDeletion } from '@/api/schemasMembers';
import { ConfirmArchiveDialog } from '@/components/ConfirmArchiveDialog';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { stagger } from '@/lib/motion';
import { deletionRemovals, deletionSummary } from './clientLifecycleModel';
import { DeletionProgress } from './DeletionProgress';

interface Props {
  org: ClientOrg;
  id: string;
  index: number;
  /** The last deletion answer of this page (its steps outlive the org, which leaves the list once deleted). */
  onDeletion: (result: ClientOrgDeletion) => void;
}

/** Deleting a client: only once suspended, confirmed by its slug; afterwards each step, the manual removals and Retry. */
export function ClientDangerZone({ org, id, index, onDeletion }: Props) {
  const [open, setOpen] = useState(false);
  const [lastSteps, setLastSteps] = useState<ClientDeletionStep[] | undefined>();
  const { remove } = useClientMutations(org.id);
  const run = async (slug: string) => {
    try {
      const result = await remove.mutateAsync(slug);
      setOpen(false);
      setLastSteps(result.steps);
      onDeletion(result);
      const s = deletionSummary(result.steps);
      if (s.state === 'failed') toast.error(`The deletion of ${org.name} stopped`, { description: s.headline });
      else toast.success(`${org.name} deleted`, { description: s.manual.length ? `${s.manual.length} provider items to remove by hand: see the checklist.` : undefined });
    } catch { /* shown in the dialog or the card */ }
  };
  const steps = org.deletion?.steps.length ? org.deletion.steps : lastSteps;
  return (
    <Card id={id} data-testid="client-danger" className="enter scroll-mt-24 border-destructive/30" style={stagger(index)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <Icon name="warning" size={20} />
          Danger zone
        </CardTitle>
        <CardDescription>Deleting a client removes it from Hangar for good</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {steps?.length ? (
          <>
            <DeletionProgress steps={steps} retrying={remove.isPending} onRetry={() => void run(org.slug)} />
            {!open ? <ProblemAlert error={remove.error} testId="client-deletion-error" /> : null}
          </>
        ) : (
          <DeleteOffer org={org} onOpen={() => setOpen(true)} />
        )}
      </CardContent>
      <ConfirmArchiveDialog
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) remove.reset(); }}
        testId="client-delete-dialog"
        title={`Delete ${org.name} for good?`}
        description="This cannot be undone. Hangar removes, in order:"
        impact={[...deletionRemovals(org).map((text) => ({ text })), { text: 'Your audit entries of this client stay in your organization.' }]}
        confirmText={org.slug}
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete client'}
        busy={remove.isPending}
        onConfirm={(typed) => void run(typed)}
      >
        <ProblemAlert error={remove.error} testId="client-delete-error" />
      </ConfirmArchiveDialog>
    </Card>
  );
}

function DeleteOffer({ org, onOpen }: { org: ClientOrg; onOpen: () => void }) {
  const suspended = org.status === 'suspended';
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h3 className="text-[15px] font-semibold text-foreground">Delete client</h3>
        <p className="mt-1 max-w-[64ch] text-[13px] text-muted-foreground">
          Removes its people's access, projects, apps, builds, environment variable values, snapshots and artifacts, and what Hangar created for it on Expo and Bitrise. Provider resources without an API are listed for you to remove by hand.
        </p>
        <p className="mt-2 text-xs text-subtle">
          {suspended ? <>Requires typing <code className="rounded-sm bg-destructive/10 px-1.5 py-px font-mono text-destructive">{org.slug}</code> to confirm</> : 'Only a suspended client can be deleted: suspend it first.'}
        </p>
      </div>
      <GatedButton variant="destructive" data-testid="client-delete-open" className="shrink-0 max-sm:h-11" blockedReason={suspended ? undefined : 'Suspend the client first'} onClick={onOpen}>
        <Icon name="delete_forever" size={18} />
        Delete client
      </GatedButton>
    </div>
  );
}
